import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Plus } from 'lucide-react';
import type { User } from '@peles/shared';
import {
  useExpenseItem,
  useUpdateExpenseItem,
  useAddExpensePayment,
  useRemoveExpensePayment,
  useExpenseConfig,
} from '../../hooks/useExpenses';
import { useDialog } from '../../context/DialogContext';
import { useVendorCategories, useVendors } from '../../hooks/useVendors';
import { useCreateVendor } from '../../hooks/useVendors';
import { useDuplicateNameGuard, isSameName } from '../../hooks/useDuplicateNameGuard';
import { VendorAutosuggest } from './VendorAutosuggest';
import { VendorQuickEditModal } from '../vendors/VendorQuickEditModal';
import { CatalogItemQuickEditModal } from '../planning/CatalogItemQuickEditModal';
import { InlineEditButton } from '../common/InlineEditButton';
import { ExpenseDocumentsSection } from './ExpenseDocumentsSection';
import type { ExpenseItemWithRelations } from '@peles/shared';

interface ExpenseDetailPanelProps {
  expenseItemId: string;
  onClose: () => void;
  onUnsavedChange: (dirty: boolean) => void;
  currentUser: User;
}

interface FormState {
  vendorCategoryId: string;
  catalogItemId: string;
  itemName: string;
  vendorName: string;
  vendorId: string;
  price: string;
  statusId: string;
  dueDate: string;
  comments: string;
}

interface PaymentFormState {
  amount: string;
  paymentMethodId: string;
  paidAt: string;
  notes: string;
}

const initialPaymentForm: PaymentFormState = {
  amount: '',
  paymentMethodId: '',
  paidAt: new Date().toISOString().split('T')[0],
  notes: '',
};

function expenseToFormState(item: ExpenseItemWithRelations): FormState {
  return {
    vendorCategoryId: item.vendorCategoryId,
    catalogItemId: item.catalogItemId || '',
    itemName: item.itemName || '',
    vendorName: item.vendorName || '',
    vendorId: item.vendorId || '',
    price: item.price.toString(),
    statusId: item.statusId,
    dueDate: item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : '',
    comments: item.comments || '',
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (
    form.vendorCategoryId !== original.vendorCategoryId ||
    form.catalogItemId !== original.catalogItemId ||
    form.itemName !== original.itemName ||
    form.vendorName !== original.vendorName ||
    form.vendorId !== original.vendorId ||
    form.price !== original.price ||
    form.statusId !== original.statusId ||
    form.dueDate !== original.dueDate ||
    form.comments !== original.comments
  );
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function ExpenseDetailPanel({
  expenseItemId,
  onClose,
  onUnsavedChange,
  currentUser: _currentUser,
}: ExpenseDetailPanelProps) {
  // currentUser is available for future permission checks
  void _currentUser;
  const { data: item, isLoading } = useExpenseItem(expenseItemId);
  const { data: config } = useExpenseConfig();
  const updateExpenseItem = useUpdateExpenseItem();
  const createVendor = useCreateVendor();
  const addPayment = useAddExpensePayment();
  const removePayment = useRemoveExpensePayment();
  const { confirm } = useDialog();
  const { data: vendors } = useVendors({ isActive: true });
  const confirmDuplicate = useDuplicateNameGuard();
  const [quickVendorId, setQuickVendorId] = useState<string | null>(null);
  const [quickCatalogItemId, setQuickCatalogItemId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    vendorCategoryId: '',
    catalogItemId: '',
    itemName: '',
    vendorName: '',
    vendorId: '',
    price: '',
    statusId: '',
    dueDate: '',
    comments: '',
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [saved, setSaved] = useState(false);
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(initialPaymentForm);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Group catalog items by category
  const filteredCatalogItems = useMemo(() => {
    if (!config || !form.vendorCategoryId) return [];
    return config.catalogItems.filter((ci) => ci.vendorCategoryId === form.vendorCategoryId);
  }, [config, form.vendorCategoryId]);

  const { data: vendorCategories = [] } = useVendorCategories();

  // Initialize form when item data loads
  useEffect(() => {
    if (item) {
      const state = expenseToFormState(item);
      setForm(state);
      setOriginalForm(state);
      setSaved(false);
    }
  }, [item]);

  // Track dirty state
  const dirty = isDirty(form, originalForm);
  useEffect(() => {
    onUnsavedChange(dirty);
  }, [dirty, onUnsavedChange]);

  const handleSave = useCallback(() => {
    const updates: Record<string, unknown> = {};
    if (form.vendorCategoryId !== originalForm.vendorCategoryId)
      updates.vendorCategoryId = form.vendorCategoryId;
    if (form.catalogItemId !== originalForm.catalogItemId)
      updates.catalogItemId = form.catalogItemId || null;
    if (form.itemName !== originalForm.itemName) updates.itemName = form.itemName || null;
    if (form.vendorName !== originalForm.vendorName) updates.vendorName = form.vendorName || null;
    if (form.vendorId !== originalForm.vendorId) updates.vendorId = form.vendorId || null;
    if (form.price !== originalForm.price) updates.price = parseFloat(form.price);
    if (form.statusId !== originalForm.statusId) updates.statusId = form.statusId;
    if (form.dueDate !== originalForm.dueDate) updates.dueDate = form.dueDate || null;
    if (form.comments !== originalForm.comments) updates.comments = form.comments || null;

    updateExpenseItem.mutate(
      { id: expenseItemId, updates },
      {
        onSuccess: (updated) => {
          const state = expenseToFormState(updated);
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, expenseItemId, updateExpenseItem]);

  const handleCreateVendor = useCallback(
    async (name: string) => {
      const duplicate = (vendors ?? []).some((v) => isSameName(v.businessName, name));
      if (duplicate && !(await confirmDuplicate({ entityLabel: 'vendor', name }))) return;
      createVendor.mutate(
        { businessName: name },
        {
          onSuccess: (vendor) => {
            setForm((f) => ({ ...f, vendorName: vendor.businessName, vendorId: vendor.id }));
            setQuickVendorId(vendor.id);
          },
        },
      );
    },
    [createVendor, vendors, confirmDuplicate],
  );

  const handleAddPayment = useCallback(() => {
    if (!paymentForm.amount || !paymentForm.paymentMethodId) return;
    addPayment.mutate(
      {
        expenseItemId,
        amount: parseFloat(paymentForm.amount),
        paymentMethodId: paymentForm.paymentMethodId,
        paidAt: paymentForm.paidAt || undefined,
        notes: paymentForm.notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          setPaymentForm(initialPaymentForm);
          setShowPaymentForm(false);
        },
      },
    );
  }, [paymentForm, expenseItemId, addPayment]);

  const handleRemovePayment = useCallback(
    async (paymentId: string) => {
      if (
        await confirm({
          title: 'Remove Payment',
          message: 'Remove this payment?',
          confirmLabel: 'Remove',
        })
      ) {
        removePayment.mutate({ expenseItemId, paymentId });
      }
    },
    [expenseItemId, removePayment, confirm],
  );

  if (isLoading) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white">
        <p className="text-gray-500 text-sm">Expense item not found</p>
      </div>
    );
  }

  return (
    <div className="md:border-l border-gray-200 p-4 md:p-6 bg-white overflow-auto">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-semibold flex-1">Expense Details</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Changes saved successfully
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label
            htmlFor="detail-expense-category"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Category
          </label>
          <select
            id="detail-expense-category"
            value={form.vendorCategoryId}
            onChange={(e) =>
              setForm((f) => ({ ...f, vendorCategoryId: e.target.value, catalogItemId: '' }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select category...</option>
            {vendorCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
            {/* Ensure current value is always shown even if not in vendorCategories */}
            {form.vendorCategoryId &&
              !vendorCategories.some((c) => c.id === form.vendorCategoryId) && (
                <option value={form.vendorCategoryId}>{item.vendorCategoryName}</option>
              )}
          </select>
        </div>

        {filteredCatalogItems.length > 0 && (
          <div>
            <label
              htmlFor="detail-expense-catalog-item"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Catalog Item
            </label>
            <select
              id="detail-expense-catalog-item"
              value={form.catalogItemId}
              onChange={(e) => setForm((f) => ({ ...f, catalogItemId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">None</option>
              {filteredCatalogItems.map((ci) => (
                <option key={ci.id} value={ci.id}>
                  {ci.name}
                </option>
              ))}
            </select>
            {form.catalogItemId && (
              <div className="mt-1 flex">
                <InlineEditButton
                  onClick={() => setQuickCatalogItemId(form.catalogItemId)}
                  label="Edit item"
                  text="Edit"
                  className="ml-auto"
                />
              </div>
            )}
          </div>
        )}

        <div>
          <label
            htmlFor="detail-expense-item-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Item Name
          </label>
          <input
            id="detail-expense-item-name"
            type="text"
            value={form.itemName}
            onChange={(e) => setForm((f) => ({ ...f, itemName: e.target.value }))}
            placeholder="Optional custom item name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name</label>
          <VendorAutosuggest
            value={form.vendorName}
            vendorId={form.vendorId || null}
            onChange={(vendorName, vendorId) => {
              setForm((f) => ({ ...f, vendorName, vendorId: vendorId || '' }));
            }}
            onCreateVendor={handleCreateVendor}
            onEditVendor={(vendorId) => setQuickVendorId(vendorId)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="detail-expense-price"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Price
            </label>
            <input
              id="detail-expense-price"
              type="number"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label
              htmlFor="detail-expense-status"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Status
            </label>
            <select
              id="detail-expense-status"
              value={form.statusId}
              onChange={(e) => setForm((f) => ({ ...f, statusId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {config?.statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label
            htmlFor="detail-expense-due-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Due Date
          </label>
          <input
            id="detail-expense-due-date"
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="detail-expense-comments"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Comments
          </label>
          <textarea
            id="detail-expense-comments"
            value={form.comments}
            onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
            placeholder="Optional"
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={!dirty || updateExpenseItem.isPending}
          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateExpenseItem.isPending ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>

      {/* Payments Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Payments</h3>

        {/* Payment Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-md px-3 py-2">
            <div className="text-xs text-gray-500">Total Price</div>
            <div className="text-sm font-medium text-gray-900">{formatCurrency(item.price)}</div>
          </div>
          <div className="bg-gray-50 rounded-md px-3 py-2">
            <div className="text-xs text-gray-500">Total Paid</div>
            <div className="text-sm font-medium text-gray-900">
              {formatCurrency(item.totalPaid)}
            </div>
          </div>
          <div className="bg-gray-50 rounded-md px-3 py-2">
            <div className="text-xs text-gray-500">Balance</div>
            <div
              className={`text-sm font-medium ${item.balance > 0 ? 'text-red-600' : 'text-green-600'}`}
            >
              {formatCurrency(item.balance)}
            </div>
          </div>
        </div>

        {/* Existing Payments List */}
        {item.payments.length > 0 && (
          <ul className="space-y-2 mb-4">
            {item.payments.map((payment) => (
              <li
                key={payment.id}
                className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(payment.amount)}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">{payment.paymentMethodName}</span>
                  <span className="text-xs text-gray-400 ml-2">
                    {new Date(payment.paidAt).toLocaleDateString()}
                  </span>
                  {payment.notes && (
                    <span className="text-xs text-gray-400 ml-2">- {payment.notes}</span>
                  )}
                </div>
                <button
                  onClick={() => handleRemovePayment(payment.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                  aria-label="Remove payment"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add Payment Form */}
        {showPaymentForm ? (
          <div className="border border-gray-200 rounded-md p-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="payment-amount"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Amount *
                </label>
                <input
                  id="payment-amount"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label
                  htmlFor="payment-method"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Method *
                </label>
                <select
                  id="payment-method"
                  value={paymentForm.paymentMethodId}
                  onChange={(e) =>
                    setPaymentForm((f) => ({ ...f, paymentMethodId: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select method...</option>
                  {config?.paymentMethods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label
                htmlFor="payment-date"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Date
              </label>
              <input
                id="payment-date"
                type="date"
                value={paymentForm.paidAt}
                onChange={(e) => setPaymentForm((f) => ({ ...f, paidAt: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label
                htmlFor="payment-notes"
                className="block text-xs font-medium text-gray-700 mb-1"
              >
                Notes
              </label>
              <input
                id="payment-notes"
                type="text"
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddPayment}
                disabled={
                  !paymentForm.amount || !paymentForm.paymentMethodId || addPayment.isPending
                }
                className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addPayment.isPending ? 'Adding...' : 'Add Payment'}
              </button>
              <button
                onClick={() => {
                  setShowPaymentForm(false);
                  setPaymentForm(initialPaymentForm);
                }}
                className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowPaymentForm(true)}
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <Plus size={16} />
            Add Payment
          </button>
        )}
      </div>

      {/* Documents Section */}
      <ExpenseDocumentsSection expenseItemId={expenseItemId} />

      <VendorQuickEditModal
        vendorId={quickVendorId}
        open={!!quickVendorId}
        onClose={() => setQuickVendorId(null)}
      />
      <CatalogItemQuickEditModal
        catalogItemId={quickCatalogItemId}
        open={!!quickCatalogItemId}
        onClose={() => setQuickCatalogItemId(null)}
      />
    </div>
  );
}
